export function getTg(): any {
  return (window as any).Telegram?.WebApp;
}

// Module-level guards so StrictMode double-mount, HMR, or accidental double
// initTelegram() calls don't stack up "themeChanged" listeners.
let telegramInitialized = false;
let mediaQueryListenerAttached = false;

export function initTelegram() {
  if (telegramInitialized) return;
  telegramInitialized = true;

  const tg = getTg();
  if (!tg) {
    // Plain web (no Telegram bridge). Honour OS preference and react to changes.
    applyThemeFromSystem();
    attachSystemThemeListener();
    return;
  }

  tg.ready?.();
  tg.expand?.();
  // colorScheme can be momentarily stale right after ready(); read what we
  // have now, but the themeChanged listener below will correct it.
  applyTheme(tg.colorScheme);
  tg.onEvent?.("themeChanged", () => applyTheme(tg.colorScheme));
}

export function applyTheme(scheme: "light" | "dark" | string | undefined) {
  const value = scheme === "dark" ? "dark" : "light";
  document.documentElement.setAttribute("data-theme", value);
}

function applyThemeFromSystem() {
  const prefersDark =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  applyTheme(prefersDark ? "dark" : "light");
}

function attachSystemThemeListener() {
  if (mediaQueryListenerAttached) return;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = (e: MediaQueryListEvent) => applyTheme(e.matches ? "dark" : "light");
  // Older Safari uses addListener / removeListener; modern browsers use addEventListener.
  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", handler);
  } else if (typeof (mq as any).addListener === "function") {
    (mq as any).addListener(handler);
  }
  mediaQueryListenerAttached = true;
}

export function hapticImpact(style: "light" | "medium" | "heavy" = "light") {
  getTg()?.HapticFeedback?.impactOccurred?.(style);
}

export function confirmDialog(message: string): Promise<boolean> {
  const tg = getTg();
  if (tg?.showConfirm) {
    return new Promise((resolve) => tg.showConfirm(message, (ok: boolean) => resolve(Boolean(ok))));
  }
  return Promise.resolve(window.confirm(message));
}

export function isRequestContactSupported(): boolean {
  const tg = getTg();
  if (!tg) return false;
  if (typeof tg.requestContact !== "function") return false;
  const v = String(tg.version ?? "0");
  const [maj = 0, min = 0] = v.split(".").map((n: string) => parseInt(n, 10) || 0);
  return maj > 6 || (maj === 6 && min >= 9);
}

export function requestContact(): Promise<string | null> {
  const tg = getTg();
  if (!tg?.requestContact) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      tg.requestContact((ok: boolean, result: any) => {
        if (!ok) return resolve(null);
        const raw =
          result?.responseUnsafe?.contact?.phone_number ??
          result?.response?.contact?.phone_number ??
          null;
        if (!raw) return resolve(null);
        const digits = String(raw).replace(/[^\d]/g, "");
        resolve(digits ? "+" + digits : null);
      });
    } catch {
      resolve(null);
    }
  });
}
